import { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Tooltip from '@givebutter/primitives/tooltip';
import { Campaign, CampaignGivingSpace } from '@givebutter/types';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { datadogRum } from '@datadog/browser-rum';
import {
  faArrowRight,
  faCog,
  faDisplay,
  faFlagCheckered,
  faMessage,
  faQrcode,
  faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Player } from '@lottiefiles/react-lottie-player';
import poweredByGivebutter from 'assets/img/powered-by-givebutter.svg';
import * as confettiAnimation from 'assets/js/lotties/confetti-animation.json';
import * as goalMetConfettiAnimation from 'assets/js/lotties/goal-met-confetti.json';
import { ConnectionStatus, ConnectionStatusProps } from 'components/connection-status';
import { ErrorBoundary } from 'components/error-boundary';
import { ErrorScreen404 } from 'components/error-screen-404/ErrorScreen404';
import { ErrorScreenInvalidCampaign } from 'components/error-screen-invalid-campaign/ErrorScreenInvalidCampaign';
import { GradientCanvas } from 'components/gradient-canvas/GradientCanvas';
import { LoadingScreen } from 'components/loading-screen/LoadingScreen';
import { SettingsEditor } from 'components/settings-editor/SettingsEditor';
import { Shoutout } from 'components/shoutout';
import { motion } from 'framer-motion';
import { useCampaignIdCache } from 'hooks/useCampaignIdCache';
import { useCurrentCampaign } from 'hooks/useCurrentCampaign';
import {
  LiveDisplayQRCodeType,
  LiveDisplayQRCodeValue,
  LiveDisplaySettings,
  getDefaultLiveDisplaySettings,
  getLiveDisplayQRCodeType,
  useStore,
} from 'hooks/useStore';
import { useSynchronousQueue } from 'hooks/useSynchronousQueue';
import { useWindowSize } from 'hooks/useWindowSize';
import { AnimationItem } from 'lottie-web';
import Pusher from 'pusher-js';
import { applyGivingSpaceCreated, useLiveDisplayCampaign } from 'queries/livedisplay';
import { ThemeName, themes } from 'types/theme';

interface PusherSubscriptionError {
  type: string;
  error: string;
  status: number;
}

interface PusherSystemError {
  message: string;
  code: number;
}

const pusherWsHost = import.meta.env.VITE_LIVEDISPLAY_PUSHER_WS_HOST;
const pusherWsPort = Number(import.meta.env.VITE_LIVEDISPLAY_PUSHER_WS_PORT ?? 443);

const pusher = new Pusher(import.meta.env.VITE_LIVEDISPLAY_PUSHER_APP_KEY, {
  cluster: import.meta.env.VITE_LIVEDISPLAY_PUSHER_APP_CLUSTER,
  ...(pusherWsHost
    ? {
        wsHost: pusherWsHost,
        wsPort: pusherWsPort,
        wssPort: pusherWsPort,
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        disableStats: true,
      }
    : {}),
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const BootstrappedApp = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Tooltip.TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <App />
          <ReactQueryDevtools position="top-left" />
        </Tooltip.TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const DONATION_DELAY = 5000;
const MIN_LOAD_TIME = import.meta.env.DEV ? 0 : 3250;
const MIN_SCREEN_WIDTH = 900;
const MIN_SCREEN_HEIGHT = 600;

function App() {
  const [lottieNewDonation, setLottieNewDonation] = useState<AnimationItem | null>(null);
  const [lottieGoalMet, setLottieGoalMet] = useState<AnimationItem | null>(null);
  const [pusherState, setPusherState] = useState<ConnectionStatusProps>({
    lastUpdated: getDateLocaleString(),
    states: { current: 'initialized', previous: 'initialized' },
  });
  const [windowWidth, windowHeight] = useWindowSize();
  const [hasLoadTimePassed, setHasLoadTimePassed] = useState(false);
  const [isMouseMoving, setIsMouseMoving] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [contentDivScale, setContentDivScale] = useState(1);
  const [contentDivTranslate, setContentDivTranslate] = useState(0);
  const [qrCodeContainerRef, setQrCodeContainerRef] = useState<HTMLDivElement | null>(null);
  const [bottomScreenHiddenMarkerRef, setBottomScreenHiddenMarkerRef] =
    useState<HTMLDivElement | null>(null);
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);
  const oldWindowHeightRef = useRef(window.innerHeight);
  const { enqueue } = useSynchronousQueue(DONATION_DELAY);
  const { campaignIdCache, setCampaignIdCache } = useCampaignIdCache(state => {
    return {
      campaignIdCache: state.campaignIdCache,
      setCampaignIdCache: state.setCampaignIdCache,
    };
  });
  const { campaignSlug, campaignId, campaignIdRef } = useCurrentCampaign();
  const {
    doesIdSettingsExist,
    hasDismissedSmallScreenWarning,
    liveDisplaySettings,
    setLiveDisplaySettings,
    setHasDismissedSmallScreenWarning,
  } = useStore(state => {
    return {
      doesIdSettingsExist: !!state.campaigns[campaignId],
      hasDismissedSmallScreenWarning: state.hasDismissedSmallScreenWarning,
      liveDisplaySettings: state.campaigns[campaignId]
        ? state.campaigns[campaignId].liveDisplaySettings
        : state.campaigns.default.liveDisplaySettings,
      setHasDismissedSmallScreenWarning: state.setHasDismissedSmallScreenWarning,
      setLiveDisplaySettings: state.setLiveDisplaySettings,
    };
  });

  useEffect(() => {
    if (!doesIdSettingsExist) {
      setLiveDisplaySettings(getDefaultLiveDisplaySettings(campaignId));
    }
  }, [campaignId, setLiveDisplaySettings, doesIdSettingsExist]);

  const oldLiveDisplaySettingsRef = useRef(liveDisplaySettings);

  const { data, isLoading, isError } = useLiveDisplayCampaign({
    campaignSlug,
  });
  const qrCodeType = getLiveDisplayQRCodeType(liveDisplaySettings);
  const latestLiveDisplaySettingsRef = useRef(liveDisplaySettings);
  latestLiveDisplaySettingsRef.current = liveDisplaySettings;

  useEffect(() => {
    if (qrCodeType !== LiveDisplayQRCodeValue.PaddleRaise || data?.paddle_raise_qr_code_url) {
      return;
    }

    if (latestLiveDisplaySettingsRef.current.qrCodeType === LiveDisplayQRCodeValue.PaddleRaise) {
      setLiveDisplaySettings({
        ...latestLiveDisplaySettingsRef.current,
        showQRCode: true,
        qrCodeType: null,
      });
    }
  }, [data?.paddle_raise_qr_code_url, qrCodeType, setLiveDisplaySettings]);

  useEffect(() => {
    if (data?.id && campaignId != data?.id.toString()) {
      campaignIdRef.current = data?.id.toString();
      if (
        campaignIdCache[campaignSlug] === undefined ||
        campaignIdCache[campaignSlug].campaignId != campaignIdRef.current
      ) {
        setCampaignIdCache({ campaignId: campaignIdRef.current, campaignSlug });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, campaignId, setCampaignIdCache, campaignSlug, campaignIdCache]);

  const [setTheme] = [
    (themeName: ThemeName) => {
      setLiveDisplaySettings({
        ...liveDisplaySettings,
        theme: themes[themeName],
      });
    },
  ];

  const timeout = useRef(0);
  const onMouseMove = () => {
    setIsMouseMoving(true);
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => setIsMouseMoving(false), 3250);
  };

  useEffect(() => {
    setTimeout(() => {
      setHasLoadTimePassed(true);
    }, MIN_LOAD_TIME);
  }, []);

  useEffect(() => {
    function handlePusherStateChange(states: ConnectionStatusProps['states']) {
      setPusherState({ lastUpdated: getDateLocaleString(), states });

      if (states.current === 'disconnected' || states.current === 'failed') {
        datadogRum.addError(new Error('Pusher connection issue'), {
          pusherState: states.current,
          previousState: states.previous,
          timestamp: getDateLocaleString(),
        });
      }
    }

    const handlePusherError = (error: PusherSystemError) => {
      datadogRum.addError(new Error('Pusher connection error'), {
        error: error.message,
        errorDetails: error,
        timestamp: getDateLocaleString(),
      });
    };

    pusher.connection.bind('state_change', handlePusherStateChange);
    pusher.connection.bind('error', handlePusherError);

    return () => {
      pusher.connection.unbind('state_change', handlePusherStateChange);
      pusher.connection.unbind('error', handlePusherError);
    };
  }, []);

  useEffect(() => {
    if (!data?.id) {
      return;
    }

    function handleGivingSpaceCreated(newShoutout: CampaignGivingSpace) {
      enqueue([
        () => {
          queryClient.setQueryData<Campaign>(['campaigns', campaignSlug, 'livedisplay'], oldData =>
            applyGivingSpaceCreated(oldData, newShoutout),
          );

          queryClient.invalidateQueries(['campaigns', campaignSlug, 'livedisplay'], {
            refetchType: 'none',
          });
        },
      ]);
    }

    const channelName = `campaigns.${data.id}`;
    const channel = pusher.subscribe(channelName);

    channel.bind('pusher:subscription_error', (error: PusherSubscriptionError) => {
      datadogRum.addError(new Error('Pusher channel subscription failed'), {
        channelName,
        campaignId: data.id,
        error: error.error,
        errorDetails: error,
        timestamp: getDateLocaleString(),
      });
    });

    channel.bind('pusher:error', (error: PusherSystemError) => {
      datadogRum.addError(new Error('Pusher channel error'), {
        channelName,
        campaignId: data.id,
        error: error.message,
        errorDetails: error,
        timestamp: getDateLocaleString(),
      });
    });

    channel.bind('giving-space.created', handleGivingSpaceCreated);

    return () => {
      channel.unsubscribe();
      channel.unbind('giving-space.created', handleGivingSpaceCreated);
      channel.unbind('pusher:subscription_error');
      channel.unbind('pusher:error');
    };
  }, [campaignSlug, data?.id, enqueue]);

  const hasCampaignGoal = data?.goal ? data.goal > 0 : false;
  const isShowingAmountRaised = liveDisplaySettings.showAmountRaised;

  const hasTextToDonate = data !== undefined && data.text_to_donate !== null;
  const isTextToDonateEnabled = liveDisplaySettings.showSMSKeyword && hasTextToDonate;
  const { qrCodeLabel, qrCodeUrl } = getQRCodeDisplayState(qrCodeType, data);
  const isQRCodeEnabled = qrCodeUrl !== null;

  const showConnectionStatus = pusherState.states.current != 'connected';

  useEffect(() => {
    let animationFrameId: number | null = null;

    const detectOverlap = () => {
      if (qrCodeContainerRef && bottomScreenHiddenMarkerRef && contentRef) {
        const rect1 = qrCodeContainerRef.getBoundingClientRect();
        const rect2 = bottomScreenHiddenMarkerRef.getBoundingClientRect();
        const overlap = rect1.bottom >= rect2.top;
        const SCALE_RATE = 0.01;
        const TRANSLATE_RATE = 0.005;

        if (overlap) {
          setContentDivScale(scale => scale - SCALE_RATE);
          setContentDivTranslate(translate => translate + TRANSLATE_RATE);
          // Continue adjusting until no overlap
          animationFrameId = requestAnimationFrame(detectOverlap);
        } else if (
          (liveDisplaySettings !== oldLiveDisplaySettingsRef.current &&
            liveDisplaySettings.theme === oldLiveDisplaySettingsRef.current.theme) ||
          // detect a big difference in heights - when the window is adjusted quickly
          window.innerHeight - oldWindowHeightRef.current > 15
        ) {
          setContentDivScale(1);
          setContentDivTranslate(0);
          // Re-check for overlap after reset
          animationFrameId = requestAnimationFrame(detectOverlap);
        } else if (window.innerHeight > oldWindowHeightRef.current) {
          // slowly start to scale the content back up to normal
          setContentDivScale(scale => {
            if (scale < 1) {
              // Continue scaling up
              animationFrameId = requestAnimationFrame(detectOverlap);
              return scale + SCALE_RATE;
            }
            return scale;
          });
          setContentDivTranslate(translate => {
            if (translate > 0) return translate - TRANSLATE_RATE;
            return translate;
          });
        }
      }

      oldWindowHeightRef.current = window.innerHeight;
      oldLiveDisplaySettingsRef.current = liveDisplaySettings;
    };

    // Handle fullscreen changes (may not trigger resize)
    const handleFullscreenChange = () => {
      // Small delay to let DOM update after fullscreen transition
      setTimeout(detectOverlap, 100);
    };

    // Delay initial check to ensure DOM is ready
    const initialTimeoutId = setTimeout(detectOverlap, 100);

    window.addEventListener('resize', detectOverlap);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      clearTimeout(initialTimeoutId);
      window.removeEventListener('resize', detectOverlap);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [bottomScreenHiddenMarkerRef, contentRef, liveDisplaySettings, qrCodeContainerRef]);

  useEffect(() => {
    const goalMet = data?.raised_percentage ? data.raised_percentage >= 100 : false;

    if (goalMet && lottieGoalMet !== null) {
      lottieGoalMet.play();
    }
  }, [data?.raised_percentage, lottieGoalMet]);

  if (isError) return <ErrorScreen404 />;
  if (data?.can_use_live_display === false) return <ErrorScreenInvalidCampaign />;
  if (isLoading || !hasLoadTimePassed || campaignId === 'default') return <LoadingScreen />;

  return (
    <>
      <Player
        lottieRef={instance => {
          setLottieGoalMet(instance);
        }}
        loop={false}
        autoplay={false}
        src={JSON.stringify(goalMetConfettiAnimation)}
        className="pointer-events-none absolute top-0 left-0 z-50 w-full"
      />
      <Player
        lottieRef={instance => {
          setLottieNewDonation(instance);
        }}
        renderer="svg"
        loop={false}
        autoplay={false}
        src={JSON.stringify(confettiAnimation)}
        className="pointer-events-none absolute top-0 right-0 z-40 w-1/4"
      />
      <GradientCanvas />
      <div
        className="relative z-10 grid h-full w-full grid-cols-4 bg-cover bg-center text-white"
        onTouchStart={() => {
          onMouseMove();
        }}
        onPointerMove={() => {
          onMouseMove();
        }}
      >
        {(windowWidth < MIN_SCREEN_WIDTH || windowHeight < MIN_SCREEN_HEIGHT) &&
          !hasDismissedSmallScreenWarning && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 text-center backdrop-blur-lg">
              <div className="flex max-w-sm flex-col items-center justify-center gap-y-2 p-4">
                <div className="relative text-red-500">
                  <FontAwesomeIcon icon={faDisplay} size="3x" />
                  <FontAwesomeIcon
                    icon={faUpRightAndDownLeftFromCenter}
                    size="lg"
                    className="absolute top-[10px] left-[17px] text-red-500"
                  />
                </div>
                <div className="text-2xl font-semibold">Your browser is too small</div>
                <div>
                  Increase the dimensions of your browser to a minimum of {MIN_SCREEN_WIDTH}px by{' '}
                  {MIN_SCREEN_HEIGHT}px. If you&apos;re still having trouble, try zooming out by
                  pressing the &apos;CMD/CTRL + Minus&apos; keys.
                </div>
                <div>
                  <button
                    onClick={() => {
                      setHasDismissedSmallScreenWarning(true);
                    }}
                    className="mt-3 flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-900 hover:bg-gray-100"
                  >
                    Continue anyway
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>
            </div>
          )}

        <div
          className={`flex px-12 pt-12 ${
            liveDisplaySettings.showShoutouts ? 'col-span-3' : 'col-span-4'
          }`}
        >
          <div
            ref={setContentRef}
            className={`flex w-full origin-top flex-col gap-y-11 ${getContentContainerStyles(
              liveDisplaySettings,
              isShowingAmountRaised,
              isTextToDonateEnabled,
              isQRCodeEnabled,
            )}`}
            style={{
              transform: `scale(${contentDivScale}) translateY(-${contentDivTranslate}%)`,
            }}
          >
            <div
              className={`flex flex-col ${getCampaignTitleStyles(
                liveDisplaySettings,
                isShowingAmountRaised,
                isTextToDonateEnabled,
                isQRCodeEnabled,
              )}`}
            >
              <h1
                className={`font-medium drop-shadow-lg ${getCampaignCreatorNameStyles(
                  liveDisplaySettings,
                  isShowingAmountRaised,
                  isTextToDonateEnabled,
                  isQRCodeEnabled,
                )}`}
              >
                {data.creator_name}
              </h1>
              <h1
                className={`text-6xl font-semibold drop-shadow-lg ${getCampaignNameStyles(
                  liveDisplaySettings,
                  isShowingAmountRaised,
                  isTextToDonateEnabled,
                  isQRCodeEnabled,
                )}`}
              >
                {data.title}
              </h1>
            </div>
            {isShowingAmountRaised && (
              <div className="flex flex-col gap-y-2">
                <div className="flex flex-wrap items-end gap-x-4">
                  <span className="flex flex-wrap items-end gap-4">
                    <span className="text-7xl font-bold drop-shadow-lg xl:text-8xl 2xl:text-9xl">
                      ${maskDecimal(data.raised)}
                    </span>
                    <span className="text-xl drop-shadow-lg xl:text-3xl">
                      {maskWholeNumber(data.donors)} supporter
                      {data.donors != 1 && 's'}
                    </span>
                  </span>
                  {hasCampaignGoal && (
                    <span className="mt-4 ml-auto text-right">
                      <span className="flex items-center gap-2 text-xl font-bold drop-shadow-lg xl:text-3xl">
                        ${maskDecimal(data?.goal)}
                        <FontAwesomeIcon icon={faFlagCheckered} size="xs" />
                      </span>
                    </span>
                  )}
                </div>

                {hasCampaignGoal && (
                  <div className="rounded-full bg-black/5 p-1.5 shadow-[inset_0px_0px_67px_rgba(0,0,0,0.25)] backdrop-blur-[50px] 2xl:p-2">
                    <motion.div
                      initial={{ width: `3rem` }}
                      animate={{
                        width: `${data.raised_percentage}%`,
                      }}
                      transition={{ duration: 1.5, ease: 'easeIn' }}
                      className="h-8 max-w-full min-w-12 rounded-full bg-linear-to-b from-[#14FF91] to-[#12B76A] 2xl:h-12"
                    >
                      &nbsp;
                    </motion.div>
                  </div>
                )}
              </div>
            )}

            {(isQRCodeEnabled || isTextToDonateEnabled) && (
              <div
                ref={setQrCodeContainerRef}
                className={`flex items-center ${
                  liveDisplaySettings.showShoutouts ? 'justify-between' : 'justify-center'
                }`}
              >
                {isQRCodeEnabled && (
                  <div
                    className={`flex flex-col gap-y-2 ${getQrCodeStyles(
                      liveDisplaySettings,
                      isTextToDonateEnabled,
                    )}`}
                  >
                    <div className="flex items-center text-lg drop-shadow-lg xl:text-3xl">
                      <FontAwesomeIcon icon={faQrcode} />
                      &nbsp;{qrCodeLabel}
                    </div>
                    <div className="aspect-square rounded-2xl">
                      <div className="flex h-full w-full items-center justify-center rounded-2xl bg-black p-4">
                        <div className="w-full rounded-2xl bg-white p-4 text-center xl:p-8">
                          <img alt="QR Code" src={qrCodeUrl} className="h-full w-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isQRCodeEnabled && isTextToDonateEnabled && (
                  <div className="w-[20%]">
                    <div className="text-center text-2xl drop-shadow-lg xl:text-6xl">OR</div>
                  </div>
                )}

                {isTextToDonateEnabled && (
                  <div
                    className={`flex flex-col gap-y-2 ${getTextToDonateStyles(
                      liveDisplaySettings,
                      isQRCodeEnabled,
                    )}`}
                  >
                    <div className="flex items-center text-lg drop-shadow-lg xl:text-3xl">
                      <FontAwesomeIcon icon={faMessage} />
                      &nbsp;Text-to-Donate
                    </div>
                    <div className="flex aspect-square flex-col items-center justify-evenly rounded-2xl bg-black px-10 py-4 text-center">
                      <div className="rounded-full bg-white p-4 text-2xl font-bold text-black xl:text-3xl 2xl:text-4xl 3xl:text-5xl">
                        {data.text_to_donate}
                      </div>
                      <div className="grid w-full grid-cols-3 items-center gap-x-1">
                        <div className="h-px bg-white" />
                        <div className="text-center text-2xl xl:text-4xl 2xl:text-5xl 3xl:text-6xl">
                          to
                        </div>
                        <div className="h-px bg-white" />
                      </div>
                      <div className="text-lg leading-none! font-black lg:text-5xl xl:text-7xl 3xl:text-8xl">
                        53-555
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          ref={setBottomScreenHiddenMarkerRef}
          className="absolute right-0 bottom-0 left-0 h-12"
        />

        {liveDisplaySettings.showShoutouts && (
          <div className="relative col-span-1 overflow-hidden bg-black/25 shadow-2xl backdrop-blur-2xl">
            <Shoutout slug={campaignSlug} lottieNewDonation={lottieNewDonation} />
          </div>
        )}

        <button
          className={`${
            !isMenuVisible && isMouseMoving ? 'opacity-100' : 'pointer-events-none opacity-0'
          } ${
            showConnectionStatus ? 'bottom-12' : 'bottom-4'
          } pointer absolute left-4 z-50 rounded-2xl bg-white p-4 font-semibold text-black shadow-lg transition-all duration-300`}
          onClick={() => {
            setIsMenuVisible(true);
          }}
        >
          <FontAwesomeIcon icon={faCog} /> View Settings
        </button>

        {showConnectionStatus && (
          <div className="absolute bottom-0 left-0">
            <ConnectionStatus states={pusherState.states} lastUpdated={pusherState.lastUpdated} />
          </div>
        )}

        <div
          className={`bottom-0 ${
            liveDisplaySettings.showShoutouts ? 'p-10 backdrop-blur-3xl' : 'pr-8 pb-4'
          } absolute right-0 z-20 w-[25%] text-center`}
        >
          <img
            className={`inline-block w-full max-w-[16rem] ${
              liveDisplaySettings.showShoutouts ? '' : 'float-right'
            }`}
            src={poweredByGivebutter}
            alt="Powered by Givebutter"
          />
        </div>

        <SettingsEditor
          hasTextToDonate={hasTextToDonate}
          hasPaddleRaiseQRCode={!!data.paddle_raise_qr_code_url}
          isMenuVisible={isMenuVisible}
          setIsMenuVisible={setIsMenuVisible}
          isMouseMoving={isMouseMoving}
          themes={themes}
          setTheme={setTheme}
          liveDisplaySettings={liveDisplaySettings}
          liveDisplayUrl={data.live_display_url}
          campaignUrl={data.url}
          setLiveDisplaySettings={setLiveDisplaySettings}
        />
      </div>
    </>
  );
}

function getContentContainerStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
  isQRCodeEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !isQRCodeEnabled &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' items-center justify-center';
  }

  return styles;
}

function getQRCodeDisplayState(qrCodeType: LiveDisplayQRCodeType, campaign?: Campaign) {
  if (qrCodeType === LiveDisplayQRCodeValue.PaddleRaise) {
    return {
      qrCodeLabel: 'Paddle Raise',
      qrCodeUrl: campaign?.paddle_raise_qr_code_url ?? null,
    };
  }

  if (qrCodeType === LiveDisplayQRCodeValue.ScanToDonate) {
    return {
      qrCodeLabel: 'Scan-to-Donate',
      qrCodeUrl: campaign?.qr_code_url ?? null,
    };
  }

  return {
    qrCodeLabel: 'Scan-to-Donate',
    qrCodeUrl: null,
  };
}

function getQrCodeStyles(liveDisplaySettings: LiveDisplaySettings, isTextToDonateEnabled: boolean) {
  let styles = '';

  if (!liveDisplaySettings.showShoutouts) {
    styles += ' w-[25%]';
  } else if (isTextToDonateEnabled) {
    styles += ' w-[33%]';
  } else {
    styles += ' w-[35%]';
  }

  return styles;
}
function getTextToDonateStyles(liveDisplaySettings: LiveDisplaySettings, isQRCodeEnabled: boolean) {
  let styles = '';

  if (!liveDisplaySettings.showShoutouts) {
    styles += ' w-[25%]';
  } else if (isQRCodeEnabled) {
    styles += ' w-[33%]';
  } else {
    styles += ' w-[35%]';
  }

  return styles;
}

function getCampaignCreatorNameStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
  isQRCodeEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !isQRCodeEnabled &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' text-6xl';
  } else {
    styles += ' text-5xl';
  }

  return styles;
}

function getCampaignNameStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
  isQRCodeEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !isQRCodeEnabled &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += 'lg:text-9xl';
  } else {
    styles += 'lg:text-8xl';
  }

  return styles;
}

function getCampaignTitleStyles(
  liveDisplaySettings: LiveDisplaySettings,
  isShowingAmountRaised: boolean,
  isTextToDonateEnabled: boolean,
  isQRCodeEnabled: boolean,
) {
  let styles = '';

  if (
    !isShowingAmountRaised &&
    !isQRCodeEnabled &&
    !isTextToDonateEnabled &&
    !liveDisplaySettings.showShoutouts
  ) {
    styles += ' gap-y-8 text-center';
  } else {
    styles += 'gap-y-2 ';
  }

  return styles;
}

function getDateLocaleString() {
  return new Date(Date.now()).toLocaleString();
}

export { themes };

export default BootstrappedApp;
